// ./src/pages/private/DisplayLayoutsPage/DisplayLayoutsPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { useDisplayLayouts } from "@/hooks";
import { DisplayLayout } from "@/utils/api";

import { DashboardLayout } from "@/components/layouts";

import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import { DisplayLayoutsTable, LayoutFormModal } from "@/components/features";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const DisplayLayoutsPage: React.FC<PageProps> = (props: PageProps) => {
  // Local search state
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Use the display layouts hook for all data management
  const {
    // Data state
    displayLayouts,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    filters,
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingDisplayLayoutId,
    selectedDisplayLayout,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // UI state management
    setSelectedDisplayLayout,

    // Error handling
    lastError,
    clearError,
  } = useDisplayLayouts({
    autoLoad: false, // We'll manually load layouts on mount
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [layoutToDelete, setLayoutToDelete] = useState<DisplayLayout | null>(
    null
  );

  // Effect to load layouts on mount
  useEffect(() => {
    // Load layouts on component mount
    list({}, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
  }, []);

  // Handle search with debouncing
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchKeyword(value);

      // Apply search filter
      const newFilters = { ...filters };
      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      } else {
        delete newFilters.searchKeyword;
      }

      // Update filters and reload data
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: 20 }, sortBy, true);
    },
    [filters, setFilters, list, sortBy]
  );

  // Handle add layout button click
  const handleAddLayout = useCallback(() => {
    setSelectedDisplayLayout(null);
    setFormModalOpened(true);
  }, [setSelectedDisplayLayout]);

  // Handle edit layout
  const handleEditLayout = useCallback(
    (layout: DisplayLayout) => {
      setSelectedDisplayLayout(layout);
      setFormModalOpened(true);
    },
    [setSelectedDisplayLayout]
  );

  // Handle duplicate layout
  const handleDuplicateLayout = useCallback(
    (layout: DisplayLayout) => {
      setSelectedDisplayLayout({ ...layout, id: "" });
      setFormModalOpened(true);
    },
    [setSelectedDisplayLayout]
  );

  // Handle view layout (could navigate to layout preview page)
  const handleViewLayout = useCallback((layout: DisplayLayout) => {
    // TODO: Navigate to layout preview page or show preview modal
    console.log("View layout:", layout);
  }, []);

  // Handle remove layout button click
  const handleRemoveLayoutClick = useCallback((layout: DisplayLayout) => {
    setLayoutToDelete(layout);
    setDeleteModalOpened(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (layoutToDelete?.id) {
      const success = await remove(layoutToDelete.id);
      if (success) {
        setDeleteModalOpened(false);
        setLayoutToDelete(null);
      }
      // Error handling is managed by the hook
    }
  }, [layoutToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setLayoutToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedDisplayLayout(null);
    clearError();
  }, [setSelectedDisplayLayout, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle layout creation
  const handleCreateLayout = useCallback(
    async (layoutData: any) => {
      const result = await create(layoutData);
      if (result) {
        setFormModalOpened(false);
        setSelectedDisplayLayout(null);
      }
      return result;
    },
    [create, setSelectedDisplayLayout]
  );

  // Handle layout update
  const handleUpdateLayout = useCallback(
    async (layoutId: string, layoutData: any) => {
      const result = await update(layoutId, layoutData);
      if (result) {
        setFormModalOpened(false);
        setSelectedDisplayLayout(null);
      }
      return result;
    },
    [update, setSelectedDisplayLayout]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-layout"
                  iconName="Plus"
                  onClick={handleAddLayout}
                  disabled={isCreating || loading}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search layouts..."
                  value={searchKeyword}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Display layouts table */}
        <DisplayLayoutsTable
          displayLayouts={displayLayouts}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditLayout={handleEditLayout}
          onRemoveLayout={handleRemoveLayoutClick}
          onDuplicateLayout={handleDuplicateLayout}
          onViewLayout={handleViewLayout}
          removingLayoutId={removingDisplayLayoutId}
          size="md"
        />

        {/* Empty state when no layouts */}
        <EmptyState
          display={isEmpty && !loading}
          title="No layouts yet"
          description="Start by creating your first display layout"
          actionIcon="Plus"
          actionLabel="Add Layout"
          onAction={handleAddLayout}
        />
      </Stack>

      {/* Layout form modal for create/edit */}
      <LayoutFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        layout={selectedDisplayLayout}
        onCreateLayout={handleCreateLayout}
        onUpdateLayout={handleUpdateLayout}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Layout"
        message={`Are you sure you want to delete "${layoutToDelete?.name}"?`}
        details="This action cannot be undone. The layout configuration will be permanently removed."
        confirmInProgress={removingDisplayLayoutId === layoutToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
